import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Image,
  Result,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LoginOutlined, ReloadOutlined, TagsOutlined } from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import {
  getImageCategories,
  getImageCategoryOptions,
  ImageCategoryItem,
  ImageCategoryOption,
} from '../../services/imageCategories';
import eventBus from '../../utils/eventBus';
import { useUserInfo } from '../../contexts/UserInfoContext';
import './index.css';

const { Text, Title } = Typography;

const ImageCategories: React.FC = () => {
  const { t, currentLanguage } = useTranslation();
  const { isAuthenticated, status } = useUserInfo();
  const isLoggedIn = status === 'loading' ? null : isAuthenticated;
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [items, setItems] = useState<ImageCategoryItem[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<ImageCategoryOption[]>([]);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const requestSequence = useRef(0);

  const categoryName = useCallback((category: {
    code: string;
    nameZh?: string | null;
    nameEn?: string | null;
  }) => {
    if (currentLanguage === 'zh') {
      return category.nameZh || category.nameEn || category.code;
    }
    return category.nameEn || category.nameZh || category.code;
  }, [currentLanguage]);

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      setCategoryOptions(await getImageCategoryOptions());
    } catch (loadError) {
      console.error('Failed to load image category options:', loadError);
      setError(t('imageCategories.loadFailed'));
    } finally {
      setOptionsLoading(false);
    }
  }, [t]);

  const loadItems = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError('');
    try {
      const result = await getImageCategories(page, pageSize, selectedCodes);
      if (sequence !== requestSequence.current) return;
      setItems(Array.isArray(result.data) ? result.data : []);
      setTotal(result.total || 0);
    } catch (loadError) {
      if (sequence !== requestSequence.current) return;
      console.error('Failed to load image categories:', loadError);
      setItems([]);
      setTotal(0);
      setError(t('imageCategories.loadFailed'));
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [page, pageSize, selectedCodes, t]);

  useEffect(() => {
    if (isLoggedIn) loadOptions();
  }, [isLoggedIn, loadOptions]);

  useEffect(() => {
    if (isLoggedIn) loadItems();
  }, [isLoggedIn, loadItems]);

  const selectOptions = useMemo(() => categoryOptions.map((option) => ({
    value: option.code,
    label: `${categoryName(option)} (${option.imageCount})`,
  })), [categoryName, categoryOptions]);

  const columns = useMemo<ColumnsType<ImageCategoryItem>>(() => [
    {
      title: t('imageCategories.preview'),
      key: 'preview',
      width: 92,
      fixed: 'left',
      render: (_, record) => {
        const imageUrl = record.rawImageUrl || record.imageUrl || '';
        return imageUrl ? (
          <Image
            className="image-categories-thumbnail"
            src={imageUrl}
            preview={{ src: imageUrl }}
            width={58}
            height={58}
            alt={record.fileName}
          />
        ) : <div className="image-categories-thumbnail-placeholder" />;
      },
    },
    {
      title: t('imageCategories.fileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      width: 230,
      ellipsis: true,
      render: (fileName: string) => (
        <Tooltip title={fileName}><Text strong>{fileName}</Text></Tooltip>
      ),
    },
    {
      title: t('imageCategories.directory'),
      dataIndex: 'directory',
      key: 'directory',
      width: 170,
      ellipsis: true,
      render: (directory?: string | null) => directory || t('imageCategories.defaultDirectory'),
    },
    {
      title: t('imageCategories.categories'),
      dataIndex: 'labels',
      key: 'labels',
      width: 430,
      render: (labels: ImageCategoryItem['labels'] = []) => (
        <Space size={[4, 6]} wrap>
          {labels.map((label) => (
            <Tooltip
              key={label.code}
              title={t('imageCategories.confidence', { score: Math.round(label.score * 100) })}
            >
              <Tag color={label.rank === 1 ? 'blue' : undefined}>
                {categoryName(label)} · {Math.round(label.score * 100)}%
              </Tag>
            </Tooltip>
          ))}
        </Space>
      ),
    },
    {
      title: t('imageCategories.uploadTime'),
      dataIndex: 'uploadTime',
      key: 'uploadTime',
      width: 190,
      render: (value: string) => value ? new Date(value).toLocaleString(currentLanguage === 'zh' ? 'zh-CN' : 'en-US') : '-',
    },
  ], [categoryName, currentLanguage, t]);

  if (isLoggedIn === null) {
    return <div className="image-categories-state"><Spin size="large" /></div>;
  }

  if (!isLoggedIn) {
    return (
      <div className="image-categories-page">
        <Result
          status="info"
          icon={<TagsOutlined />}
          title={t('imageCategories.loginTitle')}
          subTitle={t('imageCategories.loginDescription')}
          extra={(
            <Button type="primary" icon={<LoginOutlined />} onClick={() => eventBus.emit('openLoginModal')}>
              {t('imageCategories.loginAction')}
            </Button>
          )}
        />
      </div>
    );
  }

  return (
    <main className="image-categories-page">
      <section className="image-categories-hero">
        <Title level={2}>{t('imageCategories.title')}</Title>
        <Button icon={<ReloadOutlined />} onClick={() => Promise.all([loadOptions(), loadItems()])} loading={loading}>
          {t('imageCategories.refresh')}
        </Button>
      </section>

      <Card className="image-categories-card" bordered={false}>
        <div className="image-categories-toolbar">
          <div>
            <Text strong>{t('imageCategories.filterLabel')}</Text>
            <Text type="secondary" className="image-categories-filter-hint">
              {t('imageCategories.filterHint')}
            </Text>
          </div>
          <Select
            mode="multiple"
            allowClear
            showSearch
            maxTagCount="responsive"
            loading={optionsLoading}
            value={selectedCodes}
            options={selectOptions}
            placeholder={t('imageCategories.filterPlaceholder')}
            optionFilterProp="label"
            onChange={(values) => {
              setSelectedCodes(values);
              setPage(1);
            }}
          />
        </div>

        {error && (
          <Alert
            className="image-categories-error"
            type="error"
            showIcon
            message={error}
            action={<Button size="small" onClick={loadItems}>{t('imageCategories.retry')}</Button>}
          />
        )}

        <Table<ImageCategoryItem>
          rowKey="resultId"
          columns={columns}
          dataSource={items}
          loading={loading}
          scroll={{ x: 1112 }}
          locale={{
            emptyText: (
              <Empty description={selectedCodes.length
                ? t('imageCategories.noMatches')
                : t('imageCategories.empty')} />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (count) => t('imageCategories.total', { count }),
            onChange: (nextPage, nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(nextPageSize !== pageSize ? 1 : nextPage);
            },
          }}
        />
      </Card>
    </main>
  );
};

export default ImageCategories;
